import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Auth — session token + user. Persisted to localStorage so a refresh
// doesn't kick the user out. `setSession` is called by login/register;
// `clear` by logout or 401 handling.
export const useAuthStore = create(
  persist(
    (set) => ({
      token: '',
      user: null,
      profiles: [],
      setSession: ({ token, user, profiles }) =>
        set({ token, user: user || null, profiles: profiles || [] }),
      setUser: (user) => set({ user }),
      clear: () => set({ token: '', user: null, profiles: [] }),
    }),
    { name: 'vibe-auth' },
  ),
);

// Player — which channel the right pane is currently showing.
// `selectChannel` is the single place that opens a channel; pages call it
// and the player picks it up reactively.
export const usePlayerStore = create((set) => ({
  channel: null,
  selectChannel: (channel) => set({ channel }),
  clear: () => set({ channel: null }),
}));

// UI — modal state (login, register, password reset). Only one modal open
// at a time keeps the API trivial.
export const useUiStore = create((set) => ({
  modal: null, // 'login' | 'register' | 'reset' | null
  openModal: (modal) => set({ modal }),
  closeModal: () => set({ modal: null }),
}));

// User preferences. `timezone` is an IANA name (e.g. 'Europe/Budapest')
// for EPG display, or null to auto-derive from the signed-in user's
// country (TopBar / formatTime fall back to the browser zone when no
// country mapping exists). Persisted so the choice survives reloads.
//
// `vibe-prefs` previously held `utcOffsetMinutes`; the migration drops
// that field on load — fixed offsets break across DST, so we don't try
// to translate them.
export const usePrefsStore = create(
  persist(
    (set) => ({
      timezone: null,
      setTimezone: (timezone) => set({ timezone: timezone || null }),
    }),
    {
      name: 'vibe-prefs',
      version: 2,
      migrate: (persisted) => {
        if (!persisted || typeof persisted !== 'object') return { timezone: null };
        const { utcOffsetMinutes, timezone, ...rest } = persisted;
        return { ...rest, timezone: timezone || null };
      },
    },
  ),
);
